import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import aes from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';
import { Events, Platform } from 'ionic-angular';

import { APP_CONFIG } from '../../app.config';
import { Place } from '../../pages/place/models/place.model';
import { Store } from '../../pages/store/models/store.model';
import { UserPageService } from '../../pages/user/user-page.service';
import { FeatureIdentifier } from '../enums/feature-identifier.enum';
import { SubscriptionStatus } from '../enums/subscription-status.enum';
import { Country } from '../models/country.model';
import { Error } from '../models/error.model';
import { FeatureInTest } from '../models/feature-in-test.model';
import { LocalConfig } from '../models/local-config.model';
import { Params } from '../models/params.model';
import { UserLogged } from '../models/user-logged.model';
import { User } from '../models/user.model';
import { RequestService } from './request.service';

declare var cordova: any;
@Injectable()
export class UserService {

	private static readonly KEY: string = "RkBzdGczdEBwcDIwMTkh";

	user: UserLogged;
	localConfig: LocalConfig;

	estabSelected: Store;
	placeSelected: Place;

	private storeModules: { storeId: number, features: FeatureInTest[] };

	constructor(
		private events: Events,
		private platform: Platform,
		private requestService: RequestService,
		private storage: Storage,
		private userPageService: UserPageService
	) {
		this.requestService.setUserService(this);
	}

	loadUser(forceUpdate?: boolean): Promise<UserLogged> {
		return new Promise(async (resolve) => {
			try {
				await this.loadLocalConfig();
				if (this.user == null || forceUpdate) {
					const token: string = await this.getToken("token");
					if (token) {
						// Obtém info do usuário
						this.requestService.setAuthToken(token);
						this.user = await this.getUserInfo();

						// Para trazer selecionado já, prioriza o último. Depois, se tem somente 1. Se não, é apresentado modal para usuário escolher
						await this.selectEstablishmentOrPlace();

						console.log("user updated..");
						this.events.publish('user:updated', this.user);
					}
				}
				resolve(this.user);
			} catch (error) {
				console.error(error);
				resolve(this.user);
			}
		});
	}

	private async selectEstablishmentOrPlace(): Promise<void> {
		// Para trazer selecionado já, prioriza o último. Depois, se tem somente 1. Se não, é apresentado modal para usuário escolher
		if (this.localConfig && this.localConfig.lastEstablishmentId && this.user.establishments) {
			await this.selectStore(this.user.establishments.find((e: Store) => e.id === this.localConfig.lastEstablishmentId));
		} else if (this.localConfig && this.localConfig.lastPlaceId && this.user.places) {
			this.selectPlace(this.user.places.find((p: Place) => p.id === this.localConfig.lastPlaceId));
		} else if (this.user.establishments && this.user.establishments.length === 1) {
			await this.selectStore(this.user.establishments[0]);
		} else if (this.user.places && this.user.places.length === 1 && (!this.user.establishments || this.user.establishments.length === 0)) {
			this.selectPlace(this.user.places[0]);
		}
	}

	async selectStore(store: Store): Promise<void> {
		if (store) {
			this.estabSelected = new Store(store);
			this.localConfig.lastEstablishmentId = store.id;

			this.placeSelected = null;
			this.localConfig.lastPlaceId = null;

			this.updateLocalConfig();

			await this.getStoreModules();
		}
	}

	async selectPlace(place: Place): Promise<void> {
		if (place) {
			this.placeSelected = new Place(place);
			this.localConfig.lastPlaceId = place.id;

			this.estabSelected = null;
			this.localConfig.lastEstablishmentId = null;

			this.updateLocalConfig();
		}
	}

	hasFeature(featureIdentifier: FeatureIdentifier): boolean {
		if (!this.estabSelected.featureIdentifiers) return true;
		return this.estabSelected.featureIdentifiers.indexOf(featureIdentifier) > -1;
	}

	checkIfHasFeatures(features: Array<FeatureIdentifier>): boolean {
		if (!this.estabSelected) return true;

		// Estabelecimentos legados não possuem ainda esse campo
		if (!this.estabSelected.featureIdentifiers) return true;
		return this.estabSelected.featureIdentifiers.some((f) => features.indexOf(f) !== -1);
	}

	isOnlyMenuFeatureSelected(): boolean {
		return !this.estabSelected.optionForHere &&
			!this.estabSelected.offerPickup &&
			!this.estabSelected.acceptDelivery &&
			!this.estabSelected.offerDriveIn;
	}

	async getStoreModules(forceUpdate?: boolean): Promise<FeatureInTest[]> {
		try {
			if (this.estabSelected) {
				if (!this.storeModules || this.storeModules.storeId != this.estabSelected.id || forceUpdate) {
					const urlRequest: string = this.requestService.buildUrlPathParams({ storeId: this.estabSelected.id }, APP_CONFIG.WEBSERVICE.STORE_MODULES_TESTED);
					this.storeModules = {
						storeId: this.estabSelected.id,
						features: await this.requestService.getData(urlRequest) || []
					};
				}
			}
		} catch (error) {
			console.error(error);
			if (this.estabSelected) {
				return this.estabSelected.featureInTest;
			}
		}
		return this.storeModules && this.storeModules.features || [];
	}

	/**
	 * Obtem o token ou refreshToken salvos de maneira segura
	 * @param key pode ser "token" ou "refreshToken"
	 */
	private getToken(key: string): Promise<string> {
		return new Promise((resolve) => {
			// Usado na web ou como fallback caso falhar pelo plugin SecureKey
			const getTokenFromStorage: VoidFunction = async () => {
				let token: string = null;
				try {
					const tokenEncrypted: any = await this.storage.get(key);
					if (tokenEncrypted) {
						const bytes: any = aes.decrypt(tokenEncrypted, UserService.KEY);
						token = bytes.toString(Utf8);
					}
				} catch (erro) {
					console.error(erro);
				}
				resolve(token);
			};
			if (this.platform.is("cordova")) {
				cordova.plugins.SecureKeyStore.get((token) => {
					resolve(token);
				}, (error) => {
					console.log(error);
					getTokenFromStorage();
				}, key);
			} else {
				getTokenFromStorage();
			}
		});
	}

	/**
	 * Salva o token ou refreshToken de maneira segura
	 * @param key pode ser "token" ou "refreshToken"
	 * @param token chave
	 */
	private saveToken(key: string, token: string): Promise<void> {
		return new Promise((resolve) => {
			// Usado na web ou como fallback caso falhar pelo plugin SecureKey
			const setTokenToStorage: VoidFunction = () => {
				const tokenEncrypted: any = aes.encrypt(token, UserService.KEY).toString();
				this.storage.set(key, tokenEncrypted || token);
				resolve();
			};
			if (this.platform.is("cordova")) {
				cordova.plugins.SecureKeyStore.set((res) => {
					resolve();
				}, (error) => {
					console.log(error);
					setTokenToStorage();
				}, key, token);
			} else {
				setTokenToStorage();
			}
		});
	}

	/**
	 * Remove o token ou refreshToken salvos de maneira segura
	 * @param key pode ser "token" ou "refreshToken"
	 */
	private removeToken(key: string): Promise<void> {
		return new Promise((resolve) => {
			// Usado na web ou como fallback caso falhar pelo plugin SecureKey
			const removeTokenFromStorage: VoidFunction = () => {
				this.storage.remove(key);
			};
			if (this.platform.is("cordova")) {
				cordova.plugins.SecureKeyStore.remove((res) => {
					console.log(res);
					resolve();
				}, (error) => {
					console.log(error);
					removeTokenFromStorage();
				}, key);
			} else {
				removeTokenFromStorage();
			}
		});
	}

	async removeUser(): Promise<void> {
		this.user = null;
		this.estabSelected = null;
		this.placeSelected = null;
		this.storeModules = null;

		await this.loadLocalConfig();
		this.localConfig.lastEstablishmentId = null;
		this.localConfig.lastPlaceId = null;
		this.updateLocalConfig();

		this.storage.remove("user");
		this.requestService.setAuthToken(null);
		this.removeToken("token");
		this.removeToken("refreshToken");
		localStorage.removeItem("lastTimeHintPublish");
	}

	async loadLocalConfig(): Promise<void> {
		try {
			if (!this.localConfig) {
				this.localConfig = await this.storage.get("localConfig");
				if (!this.localConfig) {
					this.localConfig = new LocalConfig();
				}
			}
		} catch (error) {
			console.error(error);
			if (!this.localConfig) {
				this.localConfig = new LocalConfig();
			}
		}
	}

	async updateLocalConfig(): Promise<void> {
		await this.storage.set("localConfig", this.localConfig).catch((error) => console.log(error));
	}

	showModalSubscriptionOrFeaturesAlert(subscriptionStatus: SubscriptionStatus): void {
		this.events.publish("subscription:modalAlert", (subscriptionStatus));
	}

	showModalMaintenance(): void {
		this.events.publish("app:showModalMaintenance");
	}

	// Login
	// -------------------------------------

	login(user: string, password: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const params: any = {
				username: user,
				password: password,
				grant_type: "password",
				client_type: "partner"
			};
			this.requestService.addParamsHeader('Content-Type', "application/x-www-form-urlencoded");
			this.requestService.addParamsHeader('Authorization', "Basic N2QwMDhyd2owbDl2ODdkd2F6OmZhc3RnZXRBUEk=");
			this.requestService.postData(APP_CONFIG.WEBSERVICE.AUTH_TOKEN, params).then((tokenResponse: any) => {
				console.log(tokenResponse);
				if (tokenResponse && tokenResponse.access_token) {
					this.requestService.setAuthToken(tokenResponse.access_token);
					this.getUserInfo().then(async (userResponse: UserLogged) => {
						this.user = userResponse;
						this.saveToken("token", tokenResponse.access_token);
						this.saveToken("refreshToken", tokenResponse.refresh_token);

						await this.selectEstablishmentOrPlace();
						resolve();
					}, (error: Error) => {
						reject(error);
					});
				} else {
					reject();
				}
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	refreshToken(): Promise<void> {
		return new Promise(async (resolve, reject) => {
			const refreshToken: string = await this.getToken("refreshToken");
			const params: any = {
				refresh_token: refreshToken,
				grant_type: "refresh_token",
				client_type: "partner"
			};
			this.requestService.addParamsHeader('Content-Type', "application/x-www-form-urlencoded");
			this.requestService.addParamsHeader('Authorization', "Basic N2QwMDhyd2owbDl2ODdkd2F6OmZhc3RnZXRBUEk=");
			this.requestService.postData(APP_CONFIG.WEBSERVICE.AUTH_TOKEN, params).then((response: any) => {
				console.log(response);
				if (response && response.access_token) {
					this.requestService.setAuthToken(response.access_token);
					this.saveToken("token", response.access_token);
					this.saveToken("refreshToken", response.refresh_token);
					resolve();
				} else {
					reject();
				}
			}, (error: Error) => {
				console.error(error);
				// Springboot retorna 400 tbm quando o refresh token é inválido.
				// Para tratar tudo como 401 na controller, altero o status code para 401
				// if(erro && erro.status == 400) {
				// 	erro.status == 401;
				// }
				reject(error);
			});
		});
	}

	getUserInfo(): Promise<UserLogged> {
		return new Promise((resolve, reject) => {
			this.requestService.getData(APP_CONFIG.WEBSERVICE.USER_INFO).then((response: UserLogged) => {
				console.log(response);
				if (response && response.establishments && response.establishments.length > 0) {
					const stores: Array<Store> = [];
					response.establishments.forEach((e: Store) => {
						stores.push(new Store(e));
					});
					response.establishments = stores;

					const places: Array<Place> = [];
					response.places.forEach((p: Place) => {
						places.push(new Place(p));
					});
					response.places = places;
				}
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	logout(): Promise<void> {
		return new Promise((resolve) => {
			this.requestService.addParamsHeader('Authorization', "Basic N2QwMDhyd2owbDl2ODdkd2F6OmZhc3RnZXRBUEk=");
			this.requestService.addParamsHeader('RevokeAccessToken', this.requestService.getAuthToken());
			this.requestService.deleteData(APP_CONFIG.WEBSERVICE.AUTH_TOKEN).then((response: any) => {
				this.removeUser();
				resolve();
			}, (error: Error) => {
				this.removeUser();
				resolve();
			});
		});
	}

	sendForgotPasswordEmail(email: string): Promise<any> {
		return new Promise((resolve, reject) => {
			const params: any = {
				email: email,
				isCustomer: false
			};
			this.requestService.addParamsHeader('Content-Type', "application/x-www-form-urlencoded");
			this.requestService.postData(APP_CONFIG.WEBSERVICE.FORGOT_PASSWORD, params).then((response: any) => {
				console.log(response);
				resolve(response);
			}, (error: Error) => {
				console.log(error);
				reject(error);
			});
		});
	}

	activeAccount(token: string): Promise<any> {
		return new Promise((resolve, reject) => {
			const params: any = {
				token: token
			};
			this.requestService.addParamsHeader('Content-Type', "application/x-www-form-urlencoded");
			this.requestService.postData(APP_CONFIG.WEBSERVICE.ACTIVE_ACCOUNT, params).then((response: any) => {
				console.log(response);
				resolve(response);
			}, (error: Error) => {
				console.log(error);
				reject(error);
			});
		});
	}

	verifyPasswordToken(token: string): Promise<any> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams({ token: token }, APP_CONFIG.WEBSERVICE.VERIFY_UPDATE_PASSWORD_LINK);
			this.requestService.getData(urlRequest).then((response: any) => {
				console.log(response);
				resolve(response);
			}, (error: Error) => {
				console.log(error);
				reject(error);
			});
		});
	}

	savePassword(type: string, isCustomer: boolean, token: string, newPassword: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const data: any = {
				emailToken: token,
				password: newPassword
			};
			let urlRequest: string;
			switch (type) {
				case "reset":
					urlRequest = APP_CONFIG.WEBSERVICE.UPDATE_PASSWORD;
					data.isCustomer = isCustomer;
					break;

				case "newEstablishmentUser":
					urlRequest = APP_CONFIG.WEBSERVICE.CREATE_PASSWORD_NEW_USER;
					break;

				default: // new
					urlRequest = APP_CONFIG.WEBSERVICE.CREATE_PASSWORD;
					break;
			}
			this.requestService.addParamsHeader('Content-Type', "application/x-www-form-urlencoded");
			this.requestService.postData(urlRequest, data).then(() => {
				resolve();
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updatePassword(id: number, oldPassword: string, newPassword: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const data: any = {
				oldPassword: oldPassword,
				newPassword: newPassword,
				id: id
			};
			this.requestService.postData(APP_CONFIG.WEBSERVICE.UPDATE_PASSWORD_FROM_VIEW, data).then(() => {
				resolve();
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updateUser(user: User, storeId: number, placeId: number, updateOnlyName: boolean): Promise<void> {
		if (storeId) {
			return this.userPageService.updateStoreUser(user, storeId, updateOnlyName);
		} else {
			return this.userPageService.updatePlaceUser(user, placeId, updateOnlyName);
		}
	}

	// --------

	sendGenericEmail(title: string, body: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const data: any = {
				title: title,
				body: body
			};
			this.requestService.postData(APP_CONFIG.WEBSERVICE.SEND_GENERIC_EMAIL, data).then(() => {
				resolve();
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updateParams(params: Array<Params>): Promise<any> {
		return new Promise((resolve, reject) => {
			this.requestService.postData(APP_CONFIG.WEBSERVICE.SAVE_PARAMS, params).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getReleaseNotes(): Promise<{ version: string, news: Array<any> }[]> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = "./assets/resources/release-notes.json";
			this.requestService.getData(urlRequest, true).then(async (response: Array<any>) => {
				resolve(response);
			}, (erro: Error) => {
				console.log(erro);
				reject(erro);
			});
		});
	}

	// Countries
	// ------------------

	getCountries(): Promise<Array<Country>> {
		return new Promise((resolve, reject) => {
			this.requestService.getData("./assets/resources/country-codes.json", true).then((response: Array<Country>) => {
				resolve(response || []);
			}, (error: Error) => {
				console.log(error);
				reject(error);
			});
		});
	}

	getDefaultCountry(): Country {
		return new Country({ name: "Brazil", flag: "🇧🇷", code: "BR", dial_code: "+55" });
	}
}



// WEBPACK FOOTER //
// ./src/app/shared/services/user.service.ts